/**
 * Design tokens as TypeScript constants.
 *
 * These mirror the CSS custom properties in tokens.css exactly.
 * Use these values in web `cva` variants (`*.variants.ts` / `*.web.tsx`) and
 * wherever inline styles are required on native (`*.native.tsx` via
 * `useDsTokens()` from `../native/theme.js`, react-native-reanimated).
 */

// ── Type stacks ──────────────────────────────────────────────────────────────

export const fontStacks = {
  serif: '"Source Serif 4", "Source Serif Pro", "Iowan Old Style", Georgia, serif',
  sans: '"Inter Tight", "Inter", system-ui, -apple-system, sans-serif',
  mono: '"JetBrains Mono", "IBM Plex Mono", ui-monospace, monospace',
} as const

// ── Type scale (px values as numbers) ────────────────────────────────────────

export const textScale = {
  xs: 11,
  sm: 13,
  base: 14,
  md: 15,
  lg: 17,
  xl: 20,
  '2xl': 26,
  '3xl': 34,
  display: 48,
} as const

// ── Spacing scale (px values as numbers) ─────────────────────────────────────

export const spacing = {
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  10: 40,
  12: 48,
  // GAP-13: extend scale; naming follows N×4px convention (mirrored in tokens.css)
  16: 64,
} as const

// ── Border radii (px values as numbers) ──────────────────────────────────────

export const radii = {
  sm: 4,
  md: 8,
  lg: 14,
  full: 999,
} as const

// ── Native font family names ─────────────────────────────────────────────────
// Mirror the strings registered by `expo-font` in `apps/reader/src/theme/fonts.ts`.
// Duplicated as literals (not imported) because the design system must not
// depend on `apps/reader`; keep the two lists in sync by hand.

export const nativeFontFamilies = {
  sans: {
    regular: 'InterTight-Regular',
    medium: 'InterTight-Medium',
    semibold: 'InterTight-SemiBold',
    bold: 'InterTight-Bold',
  },
  serif: {
    regular: 'SourceSerif4-Regular',
    italic: 'SourceSerif4-Italic',
    medium: 'SourceSerif4-Medium',
    mediumItalic: 'SourceSerif4-MediumItalic',
    semibold: 'SourceSerif4-SemiBold',
    semiboldItalic: 'SourceSerif4-SemiBoldItalic',
    bold: 'SourceSerif4-Bold',
    boldItalic: 'SourceSerif4-BoldItalic',
  },
  editorial: {
    regular: 'EBGaramond-Regular',
    italic: 'EBGaramond-Italic',
    medium: 'EBGaramond-Medium',
    mediumItalic: 'EBGaramond-MediumItalic',
    semibold: 'EBGaramond-SemiBold',
    semiboldItalic: 'EBGaramond-SemiBoldItalic',
    bold: 'EBGaramond-Bold',
    boldItalic: 'EBGaramond-BoldItalic',
  },
  mono: {
    regular: 'JetBrainsMono-Regular',
    medium: 'JetBrainsMono-Medium',
    semibold: 'JetBrainsMono-SemiBold',
  },
} as const

// ── Color palettes ────────────────────────────────────────────────────────────

/** Studiolo theme — light, warm paper */
export const studiolo = {
  bg: '#f4efe6',
  bgSoft: '#ece4d4',
  surface: '#faf6ee',
  surface2: '#f1ebde',
  surface3: '#e8e0cf',
  ink: '#1a1612',
  ink2: '#4a3f33',
  ink3: '#7a6d5c',
  ink4: '#a89a85',
  line: '#d9cdb6',
  line2: '#c5b69c',
  accent: '#a8472a',
  accentSoft: '#d99373',
  accentBg: '#f0d9c8',
  gold: '#b88a3a',
  green: '#5a7a3e',
  red: '#a83a2a',
  blue: '#3e5a7a',
  /** Text color on a solid `accent` fill (button-accent, DESIGN.md's
   *  `components.button-accent.textColor`) — same literal in both themes,
   *  mirrors `tokens.css`'s `.btn-accent { color: #fff }`. */
  onAccent: '#ffffff',
  /** Tenue background tints for the quiz correct/wrong/skipped boxes —
   *  same derivation as `accentBg` (base hue mixed ~90% toward `surface`),
   *  computed per theme so review cards read correctly in both. */
  greenBg: '#eaeadc',
  goldBg: '#f3ebdc',
  redBg: '#f2e3da',
  /** Brand terracotta — stable alias. --accent is overwritten by shadcn on desktop;
   *  arAccent is the safe reference for brand colour across all targets. */
  arAccent: '#a8472a',
  /** Concept-map relation families (TASK-020) — structure/dynamic/contrast.
   *  relStructure reuses the "informational future category" hue reserved
   *  by `blue`; contrast verified ≥ 5.3:1 on bg/surface. */
  relStructure: '#2f5c8a',
  relDynamic: '#8a3a7a',
  relContrast: '#1f7a6a',
  relStructureBg: '#dde6f0',
  relDynamicBg: '#efdcea',
  relContrastBg: '#d8ece6',
} as const

/** Notturno theme — dark, deep */
export const notturno = {
  bg: '#14130f',
  bgSoft: '#1a1814',
  surface: '#1f1d18',
  surface2: '#26231d',
  surface3: '#2f2b24',
  ink: '#f0eadb',
  ink2: '#d4cdb9',
  ink3: '#9a9180',
  ink4: '#6a6354',
  line: '#34302a',
  line2: '#4a443b',
  accent: '#d99373',
  accentSoft: '#a8472a',
  accentBg: '#2a2017',
  gold: '#d4a96a',
  green: '#8eb56a',
  red: '#d97a6a',
  blue: '#7aa6d9',
  /** Same literal as Studiolo — see that theme's doc comment. */
  onAccent: '#ffffff',
  greenBg: '#2a2c20',
  goldBg: '#312b20',
  redBg: '#322620',
  /** Brand terracotta-warm — stable alias (same rationale as Studiolo.arAccent). */
  arAccent: '#d99373',
  /** Concept-map relation families (TASK-020) — contrast verified ≥ 5.9:1. */
  relStructure: '#7fb0e6',
  relDynamic: '#d48ac8',
  relContrast: '#5fc4ad',
  relStructureBg: '#1c2a3a',
  relDynamicBg: '#32203a',
  relContrastBg: '#1a302b',
} as const

export type ThemeName = 'studiolo' | 'notturno'
export type StudioloTokens = typeof studiolo
export type NotturonoTokens = typeof notturno

/**
 * Widened token shape (same keys, plain `string` values) for call sites that
 * accept "the active theme's tokens" regardless of which theme that is —
 * `StudioloTokens`/`NotturonoTokens` carry incompatible string-literal value
 * types, so a function typed on the narrower one can't take the other theme.
 */
export type ThemeTokens = { readonly [K in keyof StudioloTokens]: string }

export const themes = { studiolo, notturno } as const

export type ReaderThemeOption = {
  id: ThemeName
  label: string
  desc: string
  palette: {
    bg: string
    surface: string
    ink: string
    accent: string
    gold: string
    green: string
  }
  sidebarBorder: string
}

export const readerThemeOptions = [
  {
    id: 'studiolo',
    label: 'Studiolo',
    desc: 'Carta calda',
    palette: {
      bg: studiolo.bg,
      surface: studiolo.surface,
      ink: studiolo.ink,
      accent: studiolo.accent,
      gold: studiolo.gold,
      green: studiolo.green,
    },
    sidebarBorder: studiolo.line,
  },
  {
    id: 'notturno',
    label: 'Notturno',
    desc: 'Schermo scuro',
    palette: {
      bg: notturno.bg,
      surface: notturno.surface,
      ink: notturno.ink,
      accent: notturno.accent,
      gold: notturno.gold,
      green: notturno.green,
    },
    sidebarBorder: notturno.line,
  },
] as const satisfies readonly ReaderThemeOption[]
